import {
    Box,
    Button,
    Flex,
    Group,
    Modal,
    Paper,
    Stack,
    Text,
    useMatches,
    type FlexProps,
    type ModalBodyProps,
    type ModalContentProps,
    type ModalHeaderProps,
    type ModalRootProps,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconAlertCircle,
    IconTrash,
    type Icon as IconType,
} from '@tabler/icons-react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import MantineIcon from '../MantineIcon';
import classes from './MantineModal.module.css';
import { MantineModalContext } from './useMantineModalClose';

/**
 * Modal variants for common action patterns.
 * - `default`: Standard modal with no preset styling
 * - `delete`: Destructive action modal with red styling and trash icon
 */
export type MantineModalVariant = 'default' | 'delete';

/**
 * Semantic role of the modal, driving both its ARIA role and dismissal behavior.
 * - `dialog`: standard modal, dismissible by clicking outside / pressing Escape
 * - `alertdialog`: interrupts and requires an explicit decision; not dismissible
 *   by click-outside or Escape, guarding against accidental dismissal. Use for
 *   destructive confirmations and other choices that must be answered.
 */
export type MantineModalRole = 'dialog' | 'alertdialog';

const VARIANT_CONFIG: Record<
    MantineModalVariant,
    { icon?: IconType; color?: string; confirmLabel: string }
> = {
    default: {
        confirmLabel: 'Confirm',
    },
    delete: {
        icon: IconTrash,
        color: 'red',
        confirmLabel: 'Delete',
    },
};

/**
 * Generates a default description for the delete variant.
 */
const getVariantDescription = (
    variant: MantineModalVariant,
    resourceType?: string,
    resourceLabel?: string,
): string | undefined => {
    if (variant === 'delete' && resourceType) {
        if (resourceLabel) {
            return `Are you sure you want to delete the ${resourceType} "${resourceLabel}"?`;
        }
        return `Are you sure you want to delete this ${resourceType}?`;
    }
    return undefined;
};

export type MantineModalProps = {
    opened: boolean;
    onClose: () => void;
    title: React.ReactNode;
    /**
     * Modal variant for common action patterns.
     * - `delete`: Adds IconTrash, red action buttons, and auto-generates description
     * @default 'default'
     */
    variant?: MantineModalVariant;
    /**
     * Semantic role of the modal.
     * - `alertdialog`: sets `role="alertdialog"` and disables click-outside /
     *   Escape dismissal so a confirmation can't be lost by a stray click.
     *   Override the dismissal defaults per-case via `modalRootProps`.
     * Defaults to `alertdialog` for `variant="delete"` (destructive actions
     * shouldn't be dismissed by accident), otherwise `dialog`. Set explicitly
     * to opt a delete modal back into `dialog`, or to mark a non-delete modal
     * (e.g. a revoke/regenerate confirmation) as an alert.
     */
    role?: MantineModalRole;
    /**
     * The type of resource being acted upon (e.g., "chart", "dashboard", "space").
     * Used with `variant="delete"` to auto-generate description.
     */
    resourceType?: string;
    /**
     * The specific name/label of the resource being acted upon.
     * Used with `variant="delete"` and `resourceType` to generate:
     * "Are you sure you want to delete the {resourceType} "{resourceLabel}"?"
     */
    resourceLabel?: string;
    /**
     * Secondary line rendered under the title, in the header.
     * Use for context about the modal itself; `description` instead puts copy
     * in the body, where it reads as content.
     */
    subtitle?: React.ReactNode;
    icon?: IconType;
    /**
     * Modal size. Accepts Mantine's built-in sizes ('xs', 'sm', 'md', 'lg', 'xl') or a custom number/string.
     * @default 'lg'
     */
    size?: ModalRootProps['size'];
    /**
     * Enable fullscreen mode. The modal will take up nearly the entire viewport.
     * Useful for data viewers, tables, or content that needs maximum space.
     * When enabled, the body will fill available height without ScrollArea constraints.
     * @default false
     */
    fullScreen?: boolean;
    /**
     * Whether to show the close button in the header.
     * @default true
     */
    withCloseButton?: boolean;
    /**
     * Simple text description for the modal body.
     * Use this for simple confirmation dialogs instead of children.
     * Renders above children if both are provided.
     * For `variant="delete"`, this is auto-generated if `resourceType` is provided.
     */
    description?: string;
    /**
     * Modal body content. Optional if `description` is provided.
     */
    children?: React.ReactNode;
    /**
     * Handler for the primary confirm action button.
     * When provided, renders a confirm button with variant-appropriate styling.
     */
    onConfirm?: () => void;
    /**
     * Label for the confirm button.
     * @default "Confirm" for default variant, "Delete" for delete variant
     */
    confirmLabel?: string;
    /**
     * Whether the confirm button is disabled.
     * @default false
     */
    confirmDisabled?: boolean;
    /**
     * Whether the confirm button shows a loading state.
     * @default false
     */
    confirmLoading?: boolean;
    /**
     * Additional action buttons to display in the footer (right side), before the confirm button.
     * Use for secondary actions. For the primary action, prefer using `onConfirm`.
     */
    actions?: React.ReactNode;
    /**
     * Optional action buttons to display on the left side of the footer.
     * Useful for secondary actions like "New Space" or "Add" buttons.
     */
    leftActions?: React.ReactNode;
    /**
     * Action buttons to display in the header (right side, before close button).
     * Useful for fullscreen modals with export buttons, links, etc.
     */
    headerActions?: React.ReactNode;
    /**
     * Custom footer content, rendered full-width in place of the default
     * cancel/confirm button bar. `onConfirm`, `actions`, `leftActions` and
     * `cancelLabel` are ignored when set.
     */
    footer?: React.ReactNode;
    /**
     * Label for the cancel button. Set to `false` to hide the cancel button.
     * @default "Cancel"
     */
    cancelLabel?: string | false;
    /**
     * Whether the cancel button is disabled.
     * Useful when you want to prevent cancellation during async operations.
     * @default false
     */
    cancelDisabled?: boolean;
    /**
     * Custom handler for the cancel button. If not provided, defaults to `onClose`.
     * Useful when cancel should do something different (e.g., go back to a previous step).
     */
    onCancel?: () => void;
    /**
     * When true, closing the modal will show a confirmation dialog first.
     * Useful for forms with unsaved changes.
     * @default false
     */
    confirmBeforeClose?: boolean;
    modalRootProps?: Partial<ModalRootProps>;
    modalContentProps?: Partial<ModalContentProps>;
    modalHeaderProps?: Partial<ModalHeaderProps>;
    modalBodyProps?: Partial<ModalBodyProps>;
    modalActionsProps?: Partial<FlexProps>;
    /**
     * Max-height for the body's ScrollArea in standard (non-fullscreen) mode.
     * Use this to grow the modal vertically when the content needs more room.
     * Ignored when `fullScreen` is true.
     * @default 'calc(80vh - 140px)'
     */
    bodyScrollAreaMaxHeight?: string;
};

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';
// Escape key events a modal has already acted on in this dispatch: a
// confirmation opened by that close would otherwise catch the same key press
const handledEscapeEvents = new WeakSet<KeyboardEvent>();

/**
 * Closes on Escape only while the dialog inside `ref` is the topmost open
 * dialog, so nested dialogs (Mantine's or ours) do not close their host too.
 */
const useTopmostDialogEscape = (
    ref: React.RefObject<HTMLDivElement | null>,
    enabled: boolean,
    onEscape: () => void,
) => {
    useEffect(() => {
        if (!enabled) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (
                event.key !== 'Escape' ||
                event.isComposing ||
                event.defaultPrevented
            )
                return;
            if (handledEscapeEvents.has(event)) return;
            const target = event.target as HTMLElement | null;
            if (
                target?.getAttribute?.('data-mantine-stop-propagation') ===
                'true'
            )
                return;
            // Mantine hands the ref to the wrapper around the dialog element
            const ownDialog = ref.current?.matches(DIALOG_SELECTOR)
                ? ref.current
                : ref.current?.querySelector(DIALOG_SELECTOR);
            if (!ownDialog) return;
            // The focus trap keeps focus inside the topmost dialog
            const active = document.activeElement;
            if (
                active &&
                active !== document.body &&
                !ownDialog.contains(active)
            ) {
                return;
            }
            const dialogs = document.querySelectorAll(DIALOG_SELECTOR);
            if (dialogs[dialogs.length - 1] !== ownDialog) return;
            handledEscapeEvents.add(event);
            onEscape();
        };
        // Run after the focused control, so an open child overlay can consume
        // Escape before its host modal considers the same key press.
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [ref, enabled, onEscape]);
};

const MantineModal: React.FC<MantineModalProps> = ({
    opened,
    onClose,
    title,
    subtitle,
    variant = 'default',
    role = variant === 'delete' ? 'alertdialog' : 'dialog',
    resourceType,
    resourceLabel,
    icon,
    size = 'lg',
    fullScreen = false,
    withCloseButton = true,
    description,
    children,
    onConfirm,
    confirmLabel,
    confirmDisabled = false,
    confirmLoading = false,
    actions,
    leftActions,
    headerActions,
    footer,
    cancelLabel = 'Cancel',
    cancelDisabled = false,
    onCancel,
    confirmBeforeClose = false,
    modalRootProps,
    modalContentProps,
    modalHeaderProps,
    modalBodyProps,
    modalActionsProps,
    bodyScrollAreaMaxHeight = 'calc(80vh - 140px)',
}) => {
    const compact = useMatches(
        { base: true, sm: false },
        { getInitialValueInEffect: false },
    );
    const [
        isConfirmCloseOpen,
        { open: openConfirmClose, close: closeConfirmClose },
    ] = useDisclosure(false);

    useEffect(() => {
        if (!opened) {
            closeConfirmClose();
        }
    }, [opened, closeConfirmClose]);

    const handleClose = useCallback(() => {
        if (confirmBeforeClose) {
            openConfirmClose();
        } else {
            onClose();
        }
    }, [confirmBeforeClose, onClose, openConfirmClose]);

    // Descendants (header actions, footers, a breadcrumb in the title) close
    // the modal through the same path as its own close button.
    const closeContext = useMemo(
        () => ({ requestClose: handleClose }),
        [handleClose],
    );

    const config = VARIANT_CONFIG[variant];

    const isAlertDialog = role === 'alertdialog';
    const mobileFullScreen = compact && !isAlertDialog;

    // Mantine gives every open modal its own window Escape listener, so a
    // nested dialog (ours or a plain Mantine one) would close this modal too.
    // Escape acts here only while this content is the topmost open dialog.
    // Hosts keep the per-case override (`modalRootProps.closeOnEscape`);
    // Mantine's own listener stays off, since it cannot tell layers apart.
    const { closeOnEscape = !isAlertDialog, ...rootProps } =
        modalRootProps ?? {};
    const contentRef = useRef<HTMLDivElement>(null);
    useTopmostDialogEscape(contentRef, opened && closeOnEscape, handleClose);
    const confirmContentRef = useRef<HTMLDivElement>(null);
    useTopmostDialogEscape(
        confirmContentRef,
        isConfirmCloseOpen,
        closeConfirmClose,
    );

    const effectiveIcon = icon ?? config.icon;

    const effectiveDescription =
        description ??
        getVariantDescription(variant, resourceType, resourceLabel);

    const effectiveConfirmLabel = confirmLabel ?? config.confirmLabel;

    const confirmButtonColor = config.color;

    return (
        <MantineModalContext.Provider value={closeContext}>
            <Modal.Root
                opened={opened}
                onClose={handleClose}
                size={fullScreen ? 'auto' : size}
                centered
                closeOnClickOutside={isAlertDialog ? false : undefined}
                {...rootProps}
                closeOnEscape={false}
                fullScreen={mobileFullScreen || rootProps.fullScreen}
                yOffset={
                    mobileFullScreen
                        ? 0
                        : (rootProps.yOffset ?? (fullScreen ? 24 : undefined))
                }
                xOffset={
                    mobileFullScreen
                        ? 0
                        : (rootProps.xOffset ?? (fullScreen ? 24 : undefined))
                }
            >
                <Modal.Overlay />
                <Modal.Content
                    {...modalContentProps}
                    ref={contentRef}
                    // A form inside the modal must not submit a form around it
                    onSubmit={(event) => event.stopPropagation()}
                    role={isAlertDialog ? 'alertdialog' : undefined}
                    className={clsx(
                        !fullScreen && !mobileFullScreen && classes.content,
                        modalContentProps?.className,
                        mobileFullScreen
                            ? classes.mobileContent
                            : fullScreen && classes.fullScreenContent,
                    )}
                >
                    <Modal.Header
                        className={classes.header}
                        px={{ base: 'md', sm: 'xl' }}
                        py="md"
                        {...modalHeaderProps}
                    >
                        <Group
                            className={classes.titleGroup}
                            gap="sm"
                            flex={1}
                            // Shrinkable, so long titles truncate instead of
                            // pushing past the close button
                            miw={0}
                            wrap="nowrap"
                            align={subtitle ? 'center' : 'flex-start'}
                        >
                            {effectiveIcon ? (
                                <Paper p="6px" radius="md">
                                    <MantineIcon
                                        icon={effectiveIcon}
                                        size="md"
                                    />
                                </Paper>
                            ) : null}
                            <Stack gap={2} miw={0}>
                                <Modal.Title
                                    c="ldDark.9"
                                    fw={600}
                                    fz="md"
                                    lh="28px"
                                >
                                    {title}
                                </Modal.Title>
                                {subtitle ? (
                                    <Text c="dimmed" fz="sm" lh="20px">
                                        {subtitle}
                                    </Text>
                                ) : null}
                            </Stack>
                        </Group>
                        {headerActions ? (
                            <Group
                                className={classes.headerActions}
                                gap="sm"
                                mr={{ base: 0, sm: 'md' }}
                            >
                                {headerActions}
                            </Group>
                        ) : null}
                        {withCloseButton && (
                            <Modal.CloseButton
                                className={classes.closeButton}
                                aria-label="Close"
                                // Anchor for scope walkthroughs (data-tour-via)
                                data-tour-anchor="modal-close"
                                data-tour-hint="Close the dialog"
                            />
                        )}
                    </Modal.Header>

                    <Modal.Body
                        p={0}
                        className={classes.body}
                        mah={
                            fullScreen || mobileFullScreen
                                ? undefined
                                : bodyScrollAreaMaxHeight
                        }
                    >
                        <Stack
                            gap="md"
                            h={fullScreen ? '100%' : undefined}
                            px={modalBodyProps?.px ?? { base: 'md', sm: 'xl' }}
                            py={modalBodyProps?.py ?? 'md'}
                            bg={modalBodyProps?.bg}
                            mah={modalBodyProps?.mah}
                            mih={modalBodyProps?.mih}
                        >
                            {effectiveDescription && (
                                <Text fz="sm">{effectiveDescription}</Text>
                            )}
                            {children}
                        </Stack>
                    </Modal.Body>

                    {footer && !fullScreen ? (
                        <Box className={classes.actions} px="xl" py="md">
                            {footer}
                        </Box>
                    ) : (onConfirm || actions || leftActions) && !fullScreen ? (
                        <Flex
                            className={classes.actions}
                            px="xl"
                            py="md"
                            justify={leftActions ? 'space-between' : 'flex-end'}
                            align="center"
                            {...modalActionsProps}
                        >
                            {leftActions ?? <div />}
                            <Group gap="sm">
                                {cancelLabel !== false && (
                                    <Button
                                        variant="default"
                                        onClick={onCancel ?? handleClose}
                                        disabled={cancelDisabled}
                                    >
                                        {cancelLabel}
                                    </Button>
                                )}
                                {actions}
                                {onConfirm && (
                                    <Button
                                        color={confirmButtonColor}
                                        onClick={onConfirm}
                                        disabled={confirmDisabled}
                                        loading={confirmLoading}
                                        // Anchor for scope walkthroughs
                                        data-tour-anchor="modal-confirm"
                                        data-tour-hint="Confirm the change"
                                    >
                                        {effectiveConfirmLabel}
                                    </Button>
                                )}
                            </Group>
                        </Flex>
                    ) : null}
                </Modal.Content>
            </Modal.Root>

            {confirmBeforeClose && (
                <Modal.Root
                    opened={isConfirmCloseOpen}
                    closeOnEscape={false}
                    onClose={closeConfirmClose}
                    size="sm"
                    centered
                >
                    <Modal.Overlay />
                    <Modal.Content role="alertdialog" ref={confirmContentRef}>
                        <Modal.Header
                            className={classes.header}
                            px="xl"
                            py="md"
                        >
                            <Group gap="sm" flex={1} miw={0} wrap="nowrap">
                                <Paper p="6px" radius="md">
                                    <MantineIcon
                                        icon={IconAlertCircle}
                                        size="md"
                                    />
                                </Paper>
                                <Modal.Title
                                    c="ldDark.9"
                                    fw={600}
                                    fz="md"
                                    lh="28px"
                                >
                                    Unsaved changes
                                </Modal.Title>
                            </Group>
                            <Modal.CloseButton aria-label="Close" />
                        </Modal.Header>
                        <Modal.Body p={0}>
                            <Stack gap="md" px="xl" py="md">
                                <Text fz="sm">
                                    You have unsaved changes. Are you sure you
                                    want to close?
                                </Text>
                            </Stack>
                        </Modal.Body>
                        <Flex
                            className={classes.actions}
                            px="xl"
                            py="md"
                            justify="flex-end"
                            align="center"
                        >
                            <Group gap="sm">
                                <Button
                                    variant="default"
                                    onClick={closeConfirmClose}
                                >
                                    Keep editing
                                </Button>
                                <Button
                                    color="red"
                                    onClick={() => {
                                        closeConfirmClose();
                                        onClose();
                                    }}
                                >
                                    Discard changes
                                </Button>
                            </Group>
                        </Flex>
                    </Modal.Content>
                </Modal.Root>
            )}
        </MantineModalContext.Provider>
    );
};

export default MantineModal;
